// Vue core
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';

// Third-party libraries
import { createTooltip, destroyTooltip } from 'floating-vue';
import { debounce } from 'lodash';

// Constants
import { CHATBOT_FLOW_STATUS } from '@/consts/chatbot-flow';

// Composables
import { useSchools } from './useSchools';
import { useTags } from '@/composables/useTags';

export function useFilters(labels = [], baseFilterRef = null) {
    // refs
    const filters = ref({
        schoolId: null,
        type: null,
        name: '',
        dateAfter: null,
        dateBefore: null,
        labels: [],
        tags: [],
        messagingTrackIds: [],
        status: null
    });

    const {
        tagsOptions,
        loadingTags,
        loadTags,
        onTagCategoriesNextPage,
        handleFilterTags,
        initiateTagCategoriesCollection
    } = useTags();

    const messagingTracks = ref([]);
    const tagsTooltipInstance = ref(null);
    const messagingTracksTooltipInstance = ref(null);

    // composables
    const { schoolsList, isSchoolsLoading, loadNextPage, searchSchools, schoolsListOptions } =
        useSchools();

    // lifecycle
    onMounted(async () => {
        await schoolsList.value.refresh();

        nextTick(() => {
            updateTooltips();
        });
    });

    // computed
    const isTagsFilterDisabled = computed(() => !tagsOptions.value.length);
    const isMessagingTracksFilterDisabled = computed(() => !messagingTracks.value.length);

    // Computed properties for filter refs
    const tagsFilterRef = computed(() => baseFilterRef.value?.getFilterRef('tags'));
    const messagingTracksFilterRef = computed(() =>
        baseFilterRef.value?.getFilterRef('messagingTrackIds')
    );

    const filterOptions = computed(() => [
        {
            id: 'schoolId',
            type: 'select',
            label: 'Select school',
            hideLabel: true,
            placeholder: 'Select school',
            options: schoolsListOptions.value,
            optionLabel: 'name',
            optionValue: 'id',
            onLoadOptionsNextPage: loadNextPage,
            asyncFilter: true,
            optionsLoading: isSchoolsLoading.value
        },
        {
            id: 'name',
            type: 'text',
            label: 'Search by Flow Name',
            hideLabel: true,
            placeholder: 'Search by Flow Name'
        },
        {
            id: 'dateAfter',
            type: 'date',
            label: 'Select Date After',
            hideLabel: true,
            placeholder: 'Select Date After'
        },
        {
            id: 'dateBefore',
            type: 'date',
            label: 'Select Date Before',
            hideLabel: true,
            placeholder: 'Select Date Before'
        },
        {
            id: 'type',
            type: 'select',
            label: 'Select Type',
            hideLabel: true,
            placeholder: 'Select Type',
            options: [
                { label: 'All touchpoints', value: null },
                { label: 'Custom touchpoints', value: 'Custom' },
                { label: 'EdSights touchpoints', value: 'EdSights' }
            ]
        },
        {
            id: 'labels',
            type: 'multiselect',
            label: 'Select Labels',
            hideLabel: true,
            placeholder: 'Select Labels',
            options: labels.map((label) => ({
                label: label.text,
                value: label.id
            })),
            display: 'comma',
            filter: true
        },
        {
            id: 'status',
            type: 'select',
            label: 'Select Status',
            hideLabel: true,
            placeholder: 'Select Status',
            options: [
                CHATBOT_FLOW_STATUS.SCHEDULED,
                CHATBOT_FLOW_STATUS.INITIATED,
                CHATBOT_FLOW_STATUS.DRAFT
            ].map((status) => ({
                label: status.label,
                value: status.value
            }))
        },
        {
            id: 'tags',
            type: 'treeselect',
            disabled: isTagsFilterDisabled.value,
            label: 'Select Tags',
            hideLabel: true,
            placeholder: 'Select Tags',
            options: tagsOptions.value,
            selectionMode: 'checkbox',
            selectionLimit: 15,
            maxSelectedLabels: 1,
            asyncFilter: true,
            loading: loadingTags,
            optionLabel: 'name',
            optionValue: 'id',
            optionGroupLabel: 'name',
            optionGroupValue: 'id',
            optionGroupChildren: 'tagsRef',
            onLoadOptionsNextPage: onTagCategoriesNextPage
        },
        {
            id: 'messagingTrackIds',
            type: 'multiselect',
            disabled: isMessagingTracksFilterDisabled.value,
            label: 'Select Messaging Tracks',
            hideLabel: true,
            placeholder: 'Select Messaging Tracks',
            options: messagingTracks.value.map((messagingTrack) => ({
                label: messagingTrack.displayName,
                value: messagingTrack.id
            })),
            display: 'comma',
            filter: true,
            group: true
        }
    ]);

    // functions
    const updateTooltips = () => {
        /**
         * we have to create a new tooltip options for each filter
         * otherwise only the last filter's tooltip will be shown
         */
        const tooltipOptions = {
            content: 'Please select a school first',
            placement: 'top'
        };
        const tagsTooltipOptions = { ...tooltipOptions };
        const messagingTracksTooltipOptions = { ...tooltipOptions };

        // Clean up existing tooltips first
        if (tagsFilterRef.value && tagsTooltipInstance.value) {
            destroyTooltip(tagsFilterRef.value);
            tagsTooltipInstance.value = null;
        }

        if (messagingTracksFilterRef.value && messagingTracksTooltipInstance.value) {
            destroyTooltip(messagingTracksFilterRef.value);
            messagingTracksTooltipInstance.value = null;
        }

        // Create new tooltips if needed
        if (tagsFilterRef.value && isTagsFilterDisabled.value) {
            tagsTooltipInstance.value = createTooltip(tagsFilterRef.value.$el, tagsTooltipOptions);
        }

        if (messagingTracksFilterRef.value && isMessagingTracksFilterDisabled.value) {
            messagingTracksTooltipInstance.value = createTooltip(
                messagingTracksFilterRef.value.$el,
                messagingTracksTooltipOptions
            );
        }
    };

    const initiateTagsCollectionAndFetch = async (schoolId) => {
        await initiateTagCategoriesCollection(schoolId);
        await loadTags();
    };

    const handleAsyncFilter = debounce(async ({ filterId, value }) => {
        if (filterId === 'schoolId') {
            await searchSchools(value);
        }

        if (filterId === 'tags') {
            await handleFilterTags(value);
        }
    }, 300);

    // watchers
    watch([isTagsFilterDisabled, isMessagingTracksFilterDisabled], () => {
        updateTooltips();
    });

    // Clean up tooltip on component unmount
    onBeforeUnmount(() => {
        if (tagsFilterRef.value) {
            destroyTooltip(tagsFilterRef.value);
        }
        if (messagingTracksFilterRef.value) {
            destroyTooltip(messagingTracksFilterRef.value);
        }
    });

    return {
        filters,
        filterOptions,
        handleAsyncFilter,
        updateTooltips,
        schoolsList,
        tags: tagsOptions,
        messagingTracks,
        baseFilterRef,
        initiateTagsCollectionAndFetch
    };
}
